/** Response schemas for `/api/analytics/*`, exactly as `docs/analytics-api-contract.md` pins them. */
import { z } from 'zod';

export const METRIC_IDS = [
  'turnover',
  'wins',
  'jackpotWins',
  'ggr',
  'freeRoundPayouts',
  'netGgr',
  'rtp',
  'hold',
  'rounds',
  'activePlayers',
  'newPlayers',
  'avgBet',
  'roundsPerPlayer',
  'hitRate',
  'expectedRtp',
  'rtpDeviation',
] as const;
export type MetricId = (typeof METRIC_IDS)[number];

export const DIMENSION_IDS = [
  'hour',
  'day',
  'week',
  'month',
  'operatorId',
  'brandId',
  'gameId',
  'titleKey',
  'currency',
  'roundType',
  'channel',
] as const;
export type DimensionId = (typeof DIMENSION_IDS)[number];

export const metaSchema = z.object({
  asOf: z.string().nullable(),
  cached: z.boolean(),
  bqJobs: z.number(),
  bytesBilled: z.number(),
});
export type Meta = z.infer<typeof metaSchema>;

export const statusSchema = z.object({
  available: z.boolean(),
  asOf: z.string().nullable(),
  lagMinutes: z.number().nullable(),
  notes: z.array(z.string()),
});
export type AnalyticsStatus = z.infer<typeof statusSchema>;

const unit = z.enum(['eur', 'fraction', 'count', 'ratio', 'multiplier', 'pp']);
export type MetricUnit = z.infer<typeof unit>;

export const catalogSchema = z.object({
  metrics: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      description: z.string(),
      unit,
      direction: z.enum(['up', 'down', 'neutral']),
    }),
  ),
  dimensions: z.array(z.object({ id: z.string(), label: z.string(), maxCardinality: z.number() })),
  titles: z.array(
    z.object({
      titleKey: z.string(),
      title: z.string(),
      category: z.string().nullable(),
      type: z.string().nullable(),
      variants: z.array(
        z.object({
          gameId: z.number(),
          name: z.string(),
          theoreticalRtp: z.number().nullable(),
          rtpIssue: z.enum(['percent-like', 'out-of-range', 'missing']).nullable(),
          volatilityIndex: z.number().nullable(),
          status: z.number(),
        }),
      ),
    }),
  ),
  operators: z.array(z.object({ id: z.number(), name: z.string() })),
  brands: z.array(z.object({ id: z.string(), name: z.string(), operatorId: z.number() })),
  currencies: z.array(z.object({ code: z.string(), type: z.string(), valuable: z.boolean() })),
  meta: metaSchema,
});
export type Catalog = z.infer<typeof catalogSchema>;
export type CatalogTitle = Catalog['titles'][number];

const metricTotals = z.record(z.string(), z.number().nullable());
export type MetricTotals = Partial<Record<MetricId, number | null>>;

export const queryResultSchema = z.object({
  columns: z.array(
    z.object({
      id: z.string(),
      kind: z.enum(['dimension', 'metric']),
      label: z.string(),
      unit: z.string().optional(),
    }),
  ),
  rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))),
  totals: metricTotals,
  compare: z.object({ from: z.string(), to: z.string(), totals: metricTotals }).optional(),
  meta: metaSchema,
});
export type QueryResult = z.infer<typeof queryResultSchema>;
export type QueryRow = QueryResult['rows'][number];

export const RTP_STATUSES = ['ok', 'watch', 'serious', 'critical', 'insufficient'] as const;
export type RtpStatus = (typeof RTP_STATUSES)[number];

export const rtpHealthSchema = z.object({
  items: z.array(
    z.object({
      gameId: z.number(),
      titleKey: z.string(),
      name: z.string(),
      rounds: z.number(),
      turnover: z.number(),
      actualRtp: z.number().nullable(),
      theoreticalRtp: z.number().nullable(),
      expectedLow: z.number().nullable(),
      expectedHigh: z.number().nullable(),
      z: z.number().nullable(),
      status: z.enum(RTP_STATUSES),
    }),
  ),
  convergence: z
    .array(
      z.object({
        rounds: z.number(),
        cumulativeRtp: z.number(),
        expectedLow: z.number(),
        expectedHigh: z.number(),
      }),
    )
    .optional(),
  meta: metaSchema,
});
export type RtpHealth = z.infer<typeof rtpHealthSchema>;
export type RtpHealthItem = RtpHealth['items'][number];

export const coverageSchema = z.object({
  summary: z.object({ live: z.number(), universe: z.number() }),
  live: z.array(
    z.object({
      operatorId: z.number(),
      operatorName: z.string(),
      gameId: z.number(),
      gameName: z.string(),
      firstActive: z.string(),
      turnover: z.number(),
      ggr: z.number(),
    }),
  ),
  missing: z.array(
    z.object({
      operatorId: z.number(),
      operatorName: z.string(),
      gameId: z.number(),
      gameName: z.string(),
      platformTurnover: z.number(),
      platformGgr: z.number(),
    }),
  ),
  meta: metaSchema,
});
export type Coverage = z.infer<typeof coverageSchema>;

export const topRoundsSchema = z.object({
  rounds: z.array(
    z.object({
      roundId: z.string(),
      gameId: z.number(),
      gameName: z.string(),
      operatorId: z.number(),
      operatorName: z.string(),
      playerId: z.string(),
      currency: z.string(),
      bet: z.number(),
      win: z.number(),
      multiplier: z.number().nullable(),
      createdAt: z.string(),
    }),
  ),
  meta: metaSchema,
});
export type TopRounds = z.infer<typeof topRoundsSchema>;

export const distributionSchema = z.object({
  buckets: z.array(z.object({ from: z.number(), to: z.number().nullable(), count: z.number(), share: z.number() })),
  meta: metaSchema,
});
export type Distribution = z.infer<typeof distributionSchema>;

export const ALERT_TYPES = [
  'rtp_out_of_range',
  'traffic_drop',
  'big_win',
  'config_invalid',
  'data_freshness',
  'new_go_live',
  'went_dark',
  'player_risk',
] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

export const SEVERITIES = ['critical', 'serious', 'warning', 'info'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const ALERT_REF_KINDS = ['game', 'operator', 'brand', 'round', 'config', 'currency', 'player'] as const;

/** An entity named in an alert's text (spec 2026-10-27 §3.1). `id: null`: nothing to link to. */
export const alertRefSchema = z.object({
  kind: z.enum(ALERT_REF_KINDS),
  id: z.union([z.string(), z.number()]).nullable(),
  name: z.string(),
  titleKey: z.string().optional(),
});
export type AlertRef = z.infer<typeof alertRefSchema>;
export type AlertSegment = string | AlertRef;
const alertSegmentsSchema = z.array(z.union([z.string(), alertRefSchema]));

export const alertsSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      type: z.enum(ALERT_TYPES),
      severity: z.enum(SEVERITIES),
      title: z.string(),
      detail: z.string(),
      titleParts: alertSegmentsSchema,
      detailParts: alertSegmentsSchema,
      entity: z
        .object({
          kind: z.enum(['game', 'operator', 'round', 'config', 'platform', 'player']),
          id: z.union([z.string(), z.number()]),
          name: z.string(),
        })
        .nullable(),
      evidence: z.record(z.string(), z.union([z.number(), z.string()])),
      firstSeen: z.string(),
    }),
  ),
  meta: metaSchema,
});
export type Alerts = z.infer<typeof alertsSchema>;
export type AlertItem = Alerts['items'][number];
